import { useState, useEffect } from 'react';

import { useTranslation } from '../i18n';
import { useUpdateRoom } from '../hooks/queries/useSpaceQueries';
import { IRoomDetail } from '../types';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

interface IEditRoomDialogProps {
  isOpen: boolean;
  onClose: () => void;
  room: IRoomDetail | null;
}

const EditRoomDialog = ({ isOpen, onClose, room }: IEditRoomDialogProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const updateRoom = useUpdateRoom();

  useEffect(() => {
    if (isOpen && room) {
      setName(room.name || '');
      setDescription(room.description || '');
      setError(null);
    }
  }, [isOpen, room]);

  const handleSubmit = async () => {
    if (!room || !name.trim()) return;
    setError(null);

    updateRoom.mutate(
      {
        roomId: room.id,
        data: { name: name.trim(), description: description.trim() },
      },
      {
        onSuccess: () => onClose(),
        onError: (e) => setError(e.message || t('common.save_failed')),
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('chatroom.edit_room')}</DialogTitle>
          <DialogDescription>{t('chatroom.edit_room_desc')}</DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('chatroom.room_name')}
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('chatroom.room_name_placeholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('chatroom.description')}
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('chatroom.description_placeholder')}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || updateRoom.isPending}
          >
            {updateRoom.isPending
              ? t('common.saving')
              : t('spaces.save_changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRoomDialog;
